import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from 'components/ui/button'
import useToast from 'hooks/useToast'
import { usePartnerPayouts, usePartners } from 'hooks/usePartnerAdmin'
import { cancelPayout } from 'submission/lib/partner-api'
import PartnersLayout from './PartnersLayout'
import FulfilPayoutModal from './FulfilPayoutModal'
import { formatDate, formatUSDC } from './format'
import type { PartnerListItem, PayoutRequest, PayoutStatus } from './types'

const th =
  'text-left text-xs font-semibold uppercase text-neutral-500 px-3 py-2'
const td = 'px-3 py-2 text-sm border-t border-neutral-100'
const STATUSES: PayoutStatus[] = ['open', 'fulfilled', 'cancelled']

const PayoutsQueue: React.FC = () => {
  const { showToast } = useToast()
  const [status, setStatus] = useState<PayoutStatus>('open')
  const { data, error, isLoading, mutate } = usePartnerPayouts(status)
  const { data: partnersData } = usePartners()
  const [fulfilling, setFulfilling] = useState<PayoutRequest | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const payouts = data ?? []
  const partnersById = new Map<number, PartnerListItem>(
    (partnersData ?? []).map((p) => [p.id, p]),
  )

  const cancel = async (p: PayoutRequest) => {
    if (
      !window.confirm(
        `Cancel payout request #${p.id} for ${formatUSDC(p.amount_usdc)}?`,
      )
    )
      return
    setBusyId(p.id)
    try {
      await cancelPayout(p.id)
      showToast({ type: 'success', message: `Payout #${p.id} cancelled` })
      mutate()
    } catch (e) {
      showToast({ type: 'error', message: (e as Error).message })
    } finally {
      setBusyId(null)
    }
  }

  return (
    <PartnersLayout
      title="Payouts"
      actions={
        <select
          className="border border-neutral-300 rounded-md px-3 py-2 text-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value as PayoutStatus)}
          aria-label="Status filter"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      }
    >
      {error && (
        <div className="text-sm text-red-600 mb-3">
          Failed to load: {(error as Error).message}
        </div>
      )}
      {isLoading && <div className="text-sm text-neutral-600">Loading…</div>}
      {!isLoading && payouts.length === 0 && !error && (
        <div className="text-sm text-neutral-600">
          No {status} payout requests.
        </div>
      )}
      {payouts.length > 0 && (
        <div className="overflow-x-auto border border-neutral-200 rounded-lg">
          <table className="min-w-full">
            <thead className="bg-neutral-50">
              <tr>
                <th className={th}>#</th>
                <th className={th}>Requested</th>
                <th className={th}>Partner</th>
                <th className={th}>Amount</th>
                <th className={th}>Status</th>
                <th className={th}>Tx hash</th>
                <th className={th}>Fulfilled</th>
                <th className={th}></th>
              </tr>
            </thead>
            <tbody>
              {payouts.map((p) => {
                const partner = partnersById.get(p.partner_id)
                return (
                  <tr key={p.id}>
                    <td className={td}>{p.id}</td>
                    <td className={td}>{formatDate(p.created_at)}</td>
                    <td className={td}>
                      <Link
                        to={`/partners/${p.partner_id}`}
                        className="text-brand-primary"
                      >
                        {partner?.handle ?? `Partner #${p.partner_id}`}
                      </Link>
                      <div className="text-xs text-neutral-500">
                        {partner?.payout_wallet ? (
                          <span className="font-mono">
                            {partner.payout_wallet}
                          </span>
                        ) : (
                          <span className="text-red-600">
                            No wallet on file
                          </span>
                        )}
                        {partner?.payout_chain_id
                          ? ` · chain ${partner.payout_chain_id}`
                          : ''}
                      </div>
                    </td>
                    <td className={td}>{formatUSDC(p.amount_usdc)}</td>
                    <td className={td}>{p.status}</td>
                    <td className={`${td} font-mono text-xs`}>
                      {p.tx_hash ?? '—'}
                    </td>
                    <td className={td}>
                      {formatDate(p.fulfilled_at)}
                      {p.fulfilled_by ? ` by ${p.fulfilled_by}` : ''}
                    </td>
                    <td className={`${td} whitespace-nowrap`}>
                      {p.status === 'open' && (
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => setFulfilling(p)}
                            disabled={busyId === p.id}
                          >
                            Fulfil
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => cancel(p)}
                            disabled={busyId === p.id}
                          >
                            Cancel request
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
      <FulfilPayoutModal
        payout={fulfilling}
        partner={
          fulfilling ? partnersById.get(fulfilling.partner_id) : undefined
        }
        onClose={() => setFulfilling(null)}
        onFulfilled={() => {
          setFulfilling(null)
          mutate()
        }}
        onError={() => mutate()}
      />
    </PartnersLayout>
  )
}

export default PayoutsQueue
